import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BufferedFrameCache,
  Frame,
  readyForPlayback,
  VariableImageData,
  startBuffering,
  putInCache,
} from './caching';
import { DataSource, MapLayerToggle } from '@app/api/mapping/types';
import { AvailableData, MapDefinition, MapLayerSettings } from '@app/api/mapping/types';
import { MappingService } from '@app/api/mapping';
import { usePlaybackBuffering } from './hooks/usePlayback';
import { useMapSettings } from '@app/contexts/MapSettingsContext';
import { useDemo } from '../demo/DemoProvider';
import { textureLoadingService } from '@app/api/mapping/texture-loading-service';

export interface MapState {
  readonly datasource: DataSource | null;
  readonly map: MapDefinition;
  readonly availableLayers: MapLayerSettings[];
  readonly availableToggles: MapLayerToggle[];
  readonly availableVariables: string[] | null;
  readonly cache: BufferedFrameCache | null;
  readonly state: 'PLAYING' | 'PAUSED';
  readonly waitingForBuffer: boolean;
  readonly isPrefetching: boolean;
  readonly currentFrame: number | null;
  readonly isDragging: boolean;
  readonly pause: () => void;
  readonly play: () => void;
  readonly seek: (index: number) => void;
  readonly setDragging: (dragging: boolean) => void;
  readonly switchDataSource: (datasource: DataSource) => void;
  readonly switchMap: (map: MapDefinition) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const MapStateContext = createContext<MapState>({
  datasource: null,
  map: MappingService.availableMaps[0],
  availableLayers: [],
  availableToggles: [],
  availableVariables: null,
  cache: null,
  state: 'PAUSED',
  waitingForBuffer: false,
  isPrefetching: false,
  currentFrame: null,
  isDragging: false,
  pause: () => {},
  play: () => {},
  seek: () => {},
  setDragging: () => {},
  switchDataSource: () => {},
  switchMap: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useMapState = () => useContext(MapStateContext);

// Note: actual frame loading is implemented inside the provider using MappingService

export interface MapStateProviderProps {
  readonly children: React.ReactNode;
  readonly initialDataSource: DataSource;
  readonly initialMap: MapDefinition;
}

export function MapStateProvider({
  children,
  initialDataSource,
  initialMap,
}: MapStateProviderProps) {
  const [cache, setCache] = useState<BufferedFrameCache | null>(null);
  const [datasource, setDataSource] = useState<DataSource>(initialDataSource);
  const [map, setMap] = useState<MapDefinition>(initialMap);
  const [availableVariables, setAvailableVariables] = useState<string[] | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isPrefetching, setIsPrefetching] = useState<boolean>(false);
  const { settings: perfSettings } = useMapSettings();
  const { isDemo } = useDemo();

  const {
    state,
    currentFrame,
    waitingForBuffer,
    play: playbackPlay,
    pause,
    seek,
  } = usePlaybackBuffering(cache);

  // Track whether we've prefetched for the current datasource
  const prefetchedRef = useRef<string | null>(null);

  const settings = map.settings;

  const variablesInLayers = useMemo(() => {
    return settings.layers.map((layer) => layer.variable).sort();
  }, [settings.layers]);

  const variables = useMemo(() => {
    const varSet = new Set(variablesInLayers);
    return availableVariables?.filter((v) => varSet.has(v)).sort() ?? [];
  }, [variablesInLayers, availableVariables]);

  const availableLayers = useMemo(() => {
    const varSet = new Set(variables);
    return settings.layers.filter((l) => varSet.has(l.variable));
  }, [settings.layers, variables]);

  const availableToggles = useMemo(() => {
    const layerSet = new Set(availableLayers.map((l) => l.id));
    // Return all toggles, but mark which ones have all their variables available
    return settings.toggles.map((t) => ({
      ...t,
      available: t.layers.on == null || t.layers.on.every((l) => layerSet.has(l)),
    }));
  }, [settings.toggles, availableLayers]);

  const switchDataSource = useCallback((newDataSource: DataSource) => {
    // Immediately clear cache and reset all state to prevent stale data
    setCache(null);
    setAvailableVariables(null);
    setDataSource(newDataSource);
    // Reset prefetch tracking for new datasource
    prefetchedRef.current = null;
  }, []);

  const switchMap = useCallback((newMap: MapDefinition) => {
    // Immediately clear cache and reset all state to prevent stale data
    setCache(null);
    setMap(newMap);
  }, []);

  const setDragging = useCallback((dragging: boolean) => {
    setIsDragging(dragging);
  }, []);

  // Wrapped play function that prefetches tiles on first play
  const play = useCallback(async () => {
    // Prefetch tiles if we haven't already for this datasource
    const prefetchKey = `${datasource.model}_${datasource.forecast}`;
    if (
      prefetchedRef.current !== prefetchKey &&
      availableRef.current &&
      leadTimeIndicesRef.current.length > 0
    ) {
      prefetchedRef.current = prefetchKey;

      const leadTimes = leadTimeIndicesRef.current;
      const availableVars = availableRef.current.variables;

      console.log('▶️ Starting playback - prefetching tiles...');
      setIsPrefetching(true);

      // Wait for prefetch to complete before starting playback
      try {
        await textureLoadingService.prefetchPresignedUrls(datasource, leadTimes, availableVars);
        console.log('✅ Prefetch complete - starting playback');
      } catch (err) {
        console.warn('⚠️ Prefetch failed (will fall back to individual fetches):', err);
      } finally {
        setIsPrefetching(false);
      }
    }

    // Start playback after prefetch completes
    playbackPlay();
  }, [datasource, playbackPlay]);

  // Track latest cache and a token that increments when datasource changes
  const cacheRef = useRef<BufferedFrameCache | null>(null);
  useEffect(() => {
    cacheRef.current = cache;
  }, [cache]);

  const dataSourceTokenRef = useRef(0);
  const availableRef = useRef<AvailableData | null>(null);
  const leadTimeIndicesRef = useRef<number[]>([]);
  // Maintain a stable stride alignment while playing to avoid scheduling a new residue class each tick
  const prefetchAlignmentRef = useRef<number | null>(null);

  // Immediate reinitialization when datasource or map changes
  // This effect runs first to immediately clear all state and prevent stale data access
  useEffect(() => {
    // Invalidate all pending frame loads when datasource or map changes
    dataSourceTokenRef.current += 1;

    // Immediately clear cache to prevent stale data access
    setCache(null);

    // Clear all refs and inflight operations
    inflightFramesRef.current.clear();
    availableRef.current = null;
    leadTimeIndicesRef.current = [];
    prefetchAlignmentRef.current = null;
  }, [datasource, variablesInLayers]);

  // Reinitialize cache when datasource or map changes
  useEffect(() => {
    let isCancelled = false;
    const token = dataSourceTokenRef.current;

    const initialize = async () => {
      try {
        const available = await MappingService.loadAvailableData(datasource, isDemo);
        if (isCancelled || token !== dataSourceTokenRef.current) return;

        // Sort by prediction_timedelta (in minutes) to ensure correct order
        const sortedLeadTimes = [...available.leadTimes].sort(
          (a, b) => a.prediction_timedelta - b.prediction_timedelta,
        );

        const frames: Frame[] = sortedLeadTimes.map((lt) => ({
          datetime: new Date(lt.datetime),
          isDownloading: false,
        }));

        const newCache: BufferedFrameCache = {
          settings: {
            // Reasonable default window for interpolation pointers
            keyFrameInterval: 3,
          },
          frames,
        };

        setCache(newCache);
        seek(newCache.frames.length < 1 ? null : 0);
        setAvailableVariables(available.variables);

        // Hold available variables and map of frame index -> prediction_timedelta (in minutes)
        availableRef.current = available;
        leadTimeIndicesRef.current = sortedLeadTimes.map((lt) => lt.prediction_timedelta);
      } catch (err) {
        // If available data load fails, clear cache to reflect no playable frames
        if (isCancelled || token !== dataSourceTokenRef.current) return;
        setCache(null);
        setAvailableVariables(null);
        console.error('Failed to initialize playback cache', err);
      }
    };

    initialize();
    return () => {
      isCancelled = true;
    };
  }, [datasource, variablesInLayers, seek]);

  // Deduplicate parallel frame loads
  const inflightFramesRef = useRef<Set<number>>(new Set());

  const loadFrameData = useCallback(
    async (index: number): Promise<Record<string, VariableImageData>> => {
      if (variables.length === 0) {
        return {};
      }
      // Get the prediction_timedelta (in minutes) for this frame index
      // This corresponds to the 'index' field from backend (prediction_timedelta in minutes)
      const leadTime = leadTimeIndicesRef.current[index];
      if (leadTime == null) {
        return {};
      }

      // Load frame data - leadTime is prediction_timedelta in minutes
      const frame = await MappingService.loadFrameData(datasource, leadTime, variables);
      const transformed: Record<string, VariableImageData> = {};
      for (const [variable, data] of Object.entries(frame)) {
        transformed[variable] = {
          texture: data.textureData,
          imageType: data.imageType,
          imageUnscale: data.imageUnscale,
          bounds: data.bounds,
        };
      }
      return transformed;
    },
    [datasource, variables],
  );

  const cacheFrame = useCallback(
    async (index: number) => {
      const currentCache = cacheRef.current;
      if (currentCache == null || index < 0 || index >= currentCache.frames.length) {
        return;
      }

      const frame = currentCache.frames[index];
      if (frame.variableData != null) {
        return;
      }

      // Avoid duplicate concurrent loads for the same frame
      if (inflightFramesRef.current.has(index)) {
        return;
      }
      inflightFramesRef.current.add(index);

      // Capture token to detect datasource change
      const token = dataSourceTokenRef.current;

      // Mark frame as buffering (idempotent)
      setCache((prev) => (prev ? startBuffering(prev, index) : prev));

      try {
        const data = await loadFrameData(index);

        // Drop result if datasource changed while loading
        if (token !== dataSourceTokenRef.current) {
          return;
        }

        // Commit data to whatever the latest cache is
        setCache((prev) => (prev ? putInCache(prev, index, data) : prev));
      } finally {
        inflightFramesRef.current.delete(index);
      }
    },
    [loadFrameData],
  );

  // Helpers to reason about buffered horizon including interpolated frames
  const willBeReadyGivenKeyframes = useCallback(
    (cacheLocal: BufferedFrameCache, idx: number, keyframes: Set<number>): boolean => {
      const frame = cacheLocal.frames[idx];
      if (frame.variableData != null || frame.isDownloading || keyframes.has(idx)) {
        return true;
      }
      const k = cacheLocal.settings.keyFrameInterval;
      // Find nearest previous keyframe within interval
      let prev: number | undefined = undefined;
      for (let i = Math.max(0, idx - (k - 1)); i <= idx - 1; i += 1) {
        const f = cacheLocal.frames[i];
        if (f.variableData != null || f.isDownloading || keyframes.has(i)) {
          prev = i;
        }
      }
      if (prev == null) return false;
      // Find nearest next keyframe within interval
      let next: number | undefined = undefined;
      for (let i = idx + 1; i <= Math.min(cacheLocal.frames.length - 1, idx + (k - 1)); i += 1) {
        const f = cacheLocal.frames[i];
        if (f.variableData != null || f.isDownloading || keyframes.has(i)) {
          next = i;
          break;
        }
      }
      return next != null;
    },
    [],
  );

  const computeContiguousBufferedAhead = useCallback(
    (
      cacheLocal: BufferedFrameCache,
      startIndexExclusive: number,
      keyframes: Set<number>,
      limit: number,
    ): number => {
      if (cacheLocal.frames.length < 1) return 0;
      let count = 0;
      for (let step = 1; step <= limit; step += 1) {
        const idx = startIndexExclusive + step;
        if (idx >= cacheLocal.frames.length) {
          break; // Do not wrap for interpolation
        }
        if (!willBeReadyGivenKeyframes(cacheLocal, idx, keyframes)) {
          break;
        }
        count += 1;
      }
      return count;
    },
    [willBeReadyGivenKeyframes],
  );

  // When entering PLAYING, pick a fixed modulo alignment for keyframe prefetching
  useEffect(() => {
    if (state !== 'PLAYING') return;
    if (cache == null || currentFrame == null) return;
    const k = cache.settings.keyFrameInterval;
    if (k <= 1) {
      prefetchAlignmentRef.current = 0;
      return;
    }
    // If already set for this play session, keep it stable
    if (prefetchAlignmentRef.current != null) return;
    // Prefer an existing ready/inflight frame just ahead to anchor alignment
    const start = Math.min(cache.frames.length - 1, currentFrame + k);
    let chosenModulo: number | null = null;
    for (let i = currentFrame + 1; i <= start; i += 1) {
      const f = cache.frames[i];
      if (f.variableData != null || f.isDownloading) {
        chosenModulo = i % k;
        break;
      }
    }
    if (chosenModulo == null) {
      chosenModulo = (currentFrame + 1) % k;
    }
    prefetchAlignmentRef.current = chosenModulo;
  }, [state, cache, currentFrame]);

  // Prefetch minimal keyframes at keyFrameInterval steps when playing and horizon < maxBuffer
  useEffect(() => {
    if (state !== 'PLAYING' || cache == null || currentFrame == null) {
      return;
    }

    const k = cache.settings.keyFrameInterval;

    // Ensure the current frame is an anchor if it's not yet ready or inflight
    const current = cache.frames[currentFrame];
    if (!current.isDownloading && !readyForPlayback(current)) {
      cacheFrame(currentFrame);
    }

    // Build set of existing or inflight keyframes
    const plannedKeyframes = new Set<number>();
    cache.frames.forEach((f, i) => {
      if (f.variableData != null || f.isDownloading) {
        plannedKeyframes.add(i);
      }
    });
    // Do not treat the current frame as an anchor unless it is actually ready or inflight
    // (already covered by the loop above)

    let buffered = computeContiguousBufferedAhead(
      cache,
      currentFrame,
      plannedKeyframes,
      perfSettings.prefetchWindowSize,
    );
    if (buffered >= perfSettings.prefetchWindowSize) {
      return;
    }

    // Schedule minimal set of keyframes at strides of keyFrameInterval with stable alignment
    const scheduleTargets: number[] = [];
    const alignment = prefetchAlignmentRef.current ?? (currentFrame + 1) % k;
    // Find the first target >= currentFrame + 1 with index % k == alignment
    let firstTarget = currentFrame + 1;
    while (firstTarget < cache.frames.length && firstTarget % k !== alignment) {
      firstTarget += 1;
    }
    for (
      let target = firstTarget;
      target < cache.frames.length &&
      scheduleTargets.length <= Math.ceil(perfSettings.prefetchWindowSize / k) + 1;
      target += k
    ) {
      if (!plannedKeyframes.has(target)) {
        scheduleTargets.push(target);
      }
    }

    // Always ensure the last frame is scheduled if we're near the end
    const lastFrameIndex = cache.frames.length - 1;
    if (
      currentFrame + perfSettings.prefetchWindowSize >= lastFrameIndex &&
      !plannedKeyframes.has(lastFrameIndex) &&
      !scheduleTargets.includes(lastFrameIndex)
    ) {
      scheduleTargets.push(lastFrameIndex);
    }

    for (const target of scheduleTargets) {
      cacheFrame(target);
      plannedKeyframes.add(target);
      buffered = computeContiguousBufferedAhead(
        cache,
        currentFrame,
        plannedKeyframes,
        perfSettings.prefetchWindowSize,
      );
      if (buffered >= perfSettings.prefetchWindowSize) {
        break;
      }
    }
  }, [
    state,
    currentFrame,
    cache,
    perfSettings.prefetchWindowSize,
    cacheFrame,
    computeContiguousBufferedAhead,
  ]);

  // Load current frame if not already loading or loaded
  useEffect(() => {
    if (cache == null || currentFrame == null || state === 'PLAYING') {
      return;
    }
    const frame = cache.frames[currentFrame];
    if (frame.isDownloading || frame.variableData != null) {
      return;
    }
    cacheFrame(currentFrame);
  }, [state, cache, currentFrame, cacheFrame]);

  return (
    <MapStateContext.Provider
      value={{
        datasource,
        map,
        availableLayers,
        availableToggles,
        availableVariables,
        cache,
        state,
        waitingForBuffer,
        isPrefetching,
        currentFrame,
        isDragging,
        play,
        pause,
        seek,
        setDragging,
        switchDataSource,
        switchMap,
      }}
    >
      {children}
    </MapStateContext.Provider>
  );
}
